/**
 * ============================================================================
 * [P3-02-F] DateRangePicker - 날짜 범위 선택기 컴포넌트
 *
 * 기능:
 * - 프리셋 버튼 (7일, 30일, 90일, 1년)
 * - 커스텀 날짜 범위 선택
 * ============================================================================
 */

import { useCallback } from 'react'
import type { DateRange } from '@/hooks/useTrackingData'
import styles from './Tracking.module.css'

// ============================================================================
// Types
// ============================================================================
interface DateRangePickerProps {
  value: DateRange
  onChange: (range: DateRange) => void
}

// ============================================================================
// [P3-02-F] DateRangePicker Component
// ============================================================================
export function DateRangePicker({ value, onChange }: DateRangePickerProps) {
  // ========================================================================
  // 프리셋 핸들러
  // ========================================================================
  const handlePreset = useCallback((days: number) => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - days)
    onChange({ start, end })
  }, [onChange])

  // ========================================================================
  // 커스텀 날짜 핸들러
  // ========================================================================
  const handleStartChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, start: new Date(e.target.value) })
  }, [value, onChange])

  const handleEndChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({ ...value, end: new Date(e.target.value) })
  }, [value, onChange])

  // ========================================================================
  // 날짜 포맷 (input용)
  // ========================================================================
  const formatDate = (date: Date) => date.toISOString().split('T')[0]

  // ========================================================================
  // Render
  // ========================================================================
  return (
    <div className={styles.dateRangePicker}>
      {/* ================================================================
          프리셋 버튼
          ================================================================ */}
      <div className={styles.presetButtons}>
        <button onClick={() => handlePreset(7)}>최근 7일</button>
        <button onClick={() => handlePreset(30)}>최근 30일</button>
        <button onClick={() => handlePreset(90)}>최근 90일</button>
        <button onClick={() => handlePreset(365)}>최근 1년</button>
      </div>

      {/* ================================================================
          커스텀 날짜 선택
          ================================================================ */}
      <div className={styles.customRange}>
        <input
          type="date"
          value={formatDate(value.start)}
          onChange={handleStartChange}
          aria-label="시작 날짜"
        />
        <span>~</span>
        <input
          type="date"
          value={formatDate(value.end)}
          onChange={handleEndChange}
          aria-label="종료 날짜"
        />
      </div>
    </div>
  )
}

export default DateRangePicker
